import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelarInvitacion,
  crearInvitacion,
  listarAccesosMascota,
  listarInvitaciones,
  revocarAccesoMascota,
  RolAcceso,
  type CrearInvitacionRequest,
  type InvitacionDetalle,
} from "../api";
import { formatearFecha } from "../formato";
import CodigoQR from "./CodigoQR";

type CompartirModalProps = {
  abierto: boolean;
  onCerrar: () => void;
  mascotaID: string;
  nombreMascota: string;
};

const OPCIONES_ROL = [
  {
    rol: RolAcceso.Veterinario,
    titulo: "Veterinario / Profesional",
    icono: "🩺",
    descripcion: "Para médicos veterinarios o clínicas que atienden a tu mascota.",
    puedeEditarDefault: true,
  },
  {
    rol: RolAcceso.Cuidador,
    titulo: "Cuidador / Paseador",
    icono: "🐾",
    descripcion: "Para personas encargadas del cuidado temporal, paseos o guardería.",
    puedeEditarDefault: false,
  },
  {
    rol: RolAcceso.Lector,
    titulo: "Lector (Solo ver)",
    icono: "📖",
    descripcion: "Permite ver la ficha clínica, vacunas y recordatorios sin modificar nada.",
    puedeEditarDefault: false,
  },
  {
    rol: RolAcceso.CoDueno,
    titulo: "Co-dueño / Familiar",
    icono: "🤝",
    descripcion: "Acceso compartido con otro miembro de la familia.",
    puedeEditarDefault: true,
  },
];

const OPCIONES_DURACION_ACCESO = [
  { dias: 1, etiqueta: "1 día (24 horas — consulta puntual)" },
  { dias: 7, etiqueta: "7 días (1 semana)" },
  { dias: 30, etiqueta: "30 días (1 mes)" },
  { dias: 90, etiqueta: "3 meses" },
  { dias: 365, etiqueta: "1 año" },
  { dias: 0, etiqueta: "Permanente (sin fecha de vencimiento)" },
];

const OPCIONES_VENCIMIENTO_LINK = [
  { horas: 24, etiqueta: "24 horas" },
  { horas: 48, etiqueta: "48 horas (2 días)" },
  { horas: 168, etiqueta: "7 días" },
  { horas: 720, etiqueta: "30 días" },
];

export default function CompartirMascotaModal({
  abierto,
  onCerrar,
  mascotaID,
  nombreMascota,
}: CompartirModalProps) {
  const qc = useQueryClient();
  const [pestana, setPestana] = useState<"generar" | "accesos" | "pendientes">("generar");

  // Formulario para nueva invitación
  const [rolSeleccionado, setRolSeleccionado] = useState<RolAcceso>(RolAcceso.Veterinario);
  const [puedeEditar, setPuedeEditar] = useState(true);
  const [duracionAccesoDias, setDuracionAccesoDias] = useState<number>(7);
  const [duracionInvitacionHoras, setDuracionInvitacionHoras] = useState<number>(48);
  const [invitacionReciente, setInvitacionReciente] = useState<InvitacionDetalle | null>(null);
  const [errorGenerar, setErrorGenerar] = useState<string | null>(null);

  // Queries
  const queryAccesos = useQuery({
    queryKey: ["accesos", mascotaID],
    queryFn: () => listarAccesosMascota(mascotaID),
    enabled: abierto,
  });

  const queryInvitaciones = useQuery({
    queryKey: ["invitaciones", mascotaID],
    queryFn: () => listarInvitaciones(mascotaID),
    enabled: abierto,
  });

  // Mutaciones
  const mutacionCrear = useMutation({
    mutationFn: (req: CrearInvitacionRequest) => crearInvitacion(mascotaID, req),
    onSuccess: (nueva) => {
      setInvitacionReciente(nueva);
      setErrorGenerar(null);
      qc.invalidateQueries({ queryKey: ["invitaciones", mascotaID] });
    },
    onError: (err) => {
      setErrorGenerar(err instanceof Error ? err.message : "Error al generar invitación");
    },
  });

  const mutacionRevocar = useMutation({
    mutationFn: (accesoID: number) => revocarAccesoMascota(mascotaID, accesoID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accesos", mascotaID] });
    },
  });

  const mutacionCancelarInv = useMutation({
    mutationFn: (invID: number) => cancelarInvitacion(mascotaID, invID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitaciones", mascotaID] });
      if (invitacionReciente) setInvitacionReciente(null);
    },
  });

  if (!abierto) return null;

  const accesosActivos = (queryAccesos.data ?? []).filter((a) => a.esVigente);
  const invitacionesActivas = (queryInvitaciones.data ?? []).filter((i) => i.esValida);

  const manejarCambioRol = (nuevoRol: RolAcceso) => {
    setRolSeleccionado(nuevoRol);
    const config = OPCIONES_ROL.find((r) => r.rol === nuevoRol);
    if (config) {
      setPuedeEditar(config.puedeEditarDefault);
    }
  };

  const handleGenerar = (e: React.FormEvent) => {
    e.preventDefault();
    mutacionCrear.mutate({
      rolOtorgado: rolSeleccionado,
      puedeEditar,
      duracionAccesoDias,
      duracionInvitacionHoras,
      usosMaximos: 1,
    });
  };

  return (
    <div className="modal-backdrop" onClick={onCerrar}>
      <div className="modal-dialog panel compartir-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-cabecera">
          <div className="modal-cabecera-info">
            <span className="modal-icono-titulo">🔗</span>
            <div>
              <h2 style={{ margin: 0 }}>Compartir ficha clínica</h2>
              <p className="muted" style={{ margin: 0, fontSize: "0.875rem" }}>
                {nombreMascota}
              </p>
            </div>
          </div>
          <button type="button" className="boton-chico" onClick={onCerrar} aria-label="Cerrar modal">
            ✕
          </button>
        </header>

        <nav className="pestanas-barra" style={{ marginBottom: "1.25rem" }}>
          <button
            type="button"
            className={`pestana-boton ${pestana === "generar" ? "activa" : ""}`}
            onClick={() => setPestana("generar")}
          >
            ➕ Generar QR / Enlace
          </button>
          <button
            type="button"
            className={`pestana-boton ${pestana === "accesos" ? "activa" : ""}`}
            onClick={() => setPestana("accesos")}
          >
            👥 Personas con acceso ({accesosActivos.length})
          </button>
          <button
            type="button"
            className={`pestana-boton ${pestana === "pendientes" ? "activa" : ""}`}
            onClick={() => setPestana("pendientes")}
          >
            ⏳ Enlaces activos ({invitacionesActivas.length})
          </button>
        </nav>

        {/* Pestaña 1: Generar QR / Enlace */}
        {pestana === "generar" && (
          <div className="compartir-pestana-contenido">
            <div
              style={{
                padding: "0.75rem 1rem",
                backgroundColor: "var(--sunken)",
                borderRadius: "0.5rem",
                marginBottom: "1rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "0.75rem",
                border: "1px solid var(--borde)",
              }}
            >
              <div>
                <strong style={{ display: "block", fontSize: "0.9rem" }}>
                  🩺 Carnet de Vacunación y Ficha Pública
                </strong>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  Vista oficial lista para guarderías, paseadores o emergencias sin necesidad de iniciar sesión.
                </span>
              </div>
              <a
                href={`/mascotas/${mascotaID}/carnet`}
                className="boton-chico boton-primario"
                style={{ textDecoration: "none", whiteSpace: "nowrap" }}
                onClick={onCerrar}
              >
                Ver Carnet →
              </a>
            </div>

            {invitacionReciente ? (
              <div className="qr-resultado-panel">
                <div className="alerta-exito" style={{ marginBottom: "1rem" }}>
                  <strong>¡Código QR y enlace generados con éxito!</strong>
                </div>

                <CodigoQR
                  valor={invitacionReciente.urlInvitacion}
                  titulo={`Invitación para ${nombreMascota}`}
                />

                <div className="qr-instrucciones">
                  <p>
                    💡 <strong>¿Cómo compartir?</strong>
                  </p>
                  <ul>
                    <li>
                      Muestra este código QR en tu pantalla para que el veterinario lo escanee con
                      la cámara de su teléfono.
                    </li>
                    <li>
                      O copia el enlace y envíaselo directamente por WhatsApp o correo electrónico.
                    </li>
                    <li>
                      El receptor obtendrá acceso por{" "}
                      <strong>
                        {invitacionReciente.duracionAccesoDias > 0
                          ? `${invitacionReciente.duracionAccesoDias} días`
                          : "tiempo indefinido"}
                      </strong>{" "}
                      con permisos de{" "}
                      <strong>
                        {invitacionReciente.puedeEditar ? "lectura y edición" : "sólo lectura"}
                      </strong>
                      .
                    </li>
                  </ul>
                </div>

                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                  <button
                    type="button"
                    className="boton"
                    onClick={() => setInvitacionReciente(null)}
                  >
                    + Generar otro acceso diferente
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleGenerar} className="compartir-formulario">
                <div className="campo">
                  <label className="campo-etiqueta">
                    <strong>1. Selecciona el rol del receptor:</strong>
                  </label>
                  <div className="roles-grid">
                    {OPCIONES_ROL.map((opc) => (
                      <label
                        key={opc.rol}
                        className={`rol-tarjeta ${rolSeleccionado === opc.rol ? "seleccionado" : ""}`}
                      >
                        <input
                          type="radio"
                          name="rol"
                          value={opc.rol}
                          checked={rolSeleccionado === opc.rol}
                          onChange={() => manejarCambioRol(opc.rol)}
                          style={{ display: "none" }}
                        />
                        <div className="rol-icono">{opc.icono}</div>
                        <div className="rol-info">
                          <strong>{opc.titulo}</strong>
                          <p>{opc.descripcion}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="campo">
                  <label className="checkbox-linea">
                    <input
                      type="checkbox"
                      checked={puedeEditar}
                      onChange={(e) => setPuedeEditar(e.target.checked)}
                    />
                    <span>
                      <strong>Permitir modificar la ficha</strong> (registrar nuevas visitas,
                      vacunas, mediciones y subir documentos)
                    </span>
                  </label>
                </div>

                <div className="fila-division" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div className="campo">
                    <label className="campo-etiqueta">Duración del acceso:</label>
                    <select
                      value={duracionAccesoDias}
                      onChange={(e) => setDuracionAccesoDias(Number(e.target.value))}
                    >
                      {OPCIONES_DURACION_ACCESO.map((d) => (
                        <option key={d.dias} value={d.dias}>
                          {d.etiqueta}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="campo">
                    <label className="campo-etiqueta">El enlace vence en:</label>
                    <select
                      value={duracionInvitacionHoras}
                      onChange={(e) => setDuracionInvitacionHoras(Number(e.target.value))}
                    >
                      {OPCIONES_VENCIMIENTO_LINK.map((v) => (
                        <option key={v.horas} value={v.horas}>
                          {v.etiqueta}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {errorGenerar && <p className="err">{errorGenerar}</p>}

                <footer className="modal-acciones" style={{ marginTop: "1rem" }}>
                  <button type="button" className="boton" onClick={onCerrar}>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="boton-primario"
                    disabled={mutacionCrear.isPending}
                  >
                    {mutacionCrear.isPending ? "Generando…" : "⚡ Generar código QR y enlace"}
                  </button>
                </footer>
              </form>
            )}
          </div>
        )}

        {/* Pestaña 2: Personas con acceso */}
        {pestana === "accesos" && (
          <div className="compartir-pestana-contenido">
            {queryAccesos.isPending ? (
              <p className="muted">Cargando accesos…</p>
            ) : queryAccesos.error ? (
              <p className="err">{queryAccesos.error.message}</p>
            ) : (queryAccesos.data ?? []).length === 0 ? (
              <p className="muted">No hay personas con acceso registradas.</p>
            ) : (
              <div className="accesos-lista">
                {(queryAccesos.data ?? []).map((acc) => {
                  const esPropio = acc.esDueno;
                  return (
                    <div
                      key={acc.mascotaAccesoID}
                      className={`acceso-item-tarjeta ${!acc.esVigente ? "inactivo" : ""}`}
                    >
                      <div className="acceso-item-cab">
                        <div className="acceso-item-avatar">
                          {acc.rol === RolAcceso.Dueno
                            ? "👑"
                            : acc.rol === RolAcceso.CoDueno
                              ? "🤝"
                              : acc.rol === RolAcceso.Veterinario
                                ? "🩺"
                                : acc.rol === RolAcceso.Cuidador
                                  ? "🐾"
                                  : "📖"}
                        </div>
                        <div className="acceso-item-datos">
                          <strong>{acc.nombreCompleto}</strong>
                          <span className="muted" style={{ fontSize: "0.85rem" }}>
                            {acc.email}
                          </span>
                          <div className="acceso-badges" style={{ marginTop: "0.3rem" }}>
                            <span className="etiqueta neutra">
                              {acc.rol === RolAcceso.Dueno
                                ? "Dueño principal"
                                : acc.rol === RolAcceso.CoDueno
                                  ? "Co-dueño"
                                  : acc.rol === RolAcceso.Veterinario
                                    ? "Veterinario"
                                    : acc.rol === RolAcceso.Cuidador
                                      ? "Cuidador"
                                      : "Lector"}
                            </span>
                            <span className={`etiqueta ${acc.puedeEditar ? "al-dia" : "neutra"}`}>
                              {acc.puedeEditar ? "Lectura y edición" : "Sólo lectura"}
                            </span>
                            {acc.revocado ? (
                              <span className="etiqueta vencido">Revocado</span>
                            ) : acc.esVigente ? (
                              <span className="etiqueta al-dia">
                                {acc.hastaUtc
                                  ? `Vigente hasta ${formatearFecha(acc.hastaUtc)}`
                                  : "Vigencia permanente"}
                              </span>
                            ) : (
                              <span className="etiqueta vencido">Expirado</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {!esPropio && acc.esVigente && (
                        <div className="acceso-item-acciones">
                          <button
                            type="button"
                            className="boton-chico err"
                            disabled={mutacionRevocar.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  `¿Estás seguro de que deseas revocar el acceso a ${acc.nombreCompleto}?`,
                                )
                              ) {
                                mutacionRevocar.mutate(acc.mascotaAccesoID);
                              }
                            }}
                          >
                            ✕ Revocar acceso
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pestaña 3: Enlaces activos */}
        {pestana === "pendientes" && (
          <div className="compartir-pestana-contenido">
            {queryInvitaciones.isPending ? (
              <p className="muted">Cargando enlaces…</p>
            ) : queryInvitaciones.error ? (
              <p className="err">{queryInvitaciones.error.message}</p>
            ) : invitacionesActivas.length === 0 ? (
              <p className="muted">No hay enlaces de invitación pendientes de canjear.</p>
            ) : (
              <div className="accesos-lista">
                {invitacionesActivas.map((inv) => (
                  <div key={inv.invitacionID} className="acceso-item-tarjeta">
                    <div className="acceso-item-cab">
                      <div className="acceso-item-avatar">🔗</div>
                      <div className="acceso-item-datos">
                        <strong>
                          Invitación para{" "}
                          {inv.rolOtorgado === RolAcceso.Veterinario
                            ? "Veterinario"
                            : inv.rolOtorgado === RolAcceso.Cuidador
                              ? "Cuidador"
                              : inv.rolOtorgado === RolAcceso.CoDueno
                                ? "Co-dueño"
                                : "Lector"}
                        </strong>
                        <div className="acceso-badges" style={{ marginTop: "0.3rem" }}>
                          <span className={`etiqueta ${inv.puedeEditar ? "al-dia" : "neutra"}`}>
                            {inv.puedeEditar ? "Con edición" : "Solo lectura"}
                          </span>
                          <span className="etiqueta neutra">
                            Acceso por{" "}
                            {inv.duracionAccesoDias > 0
                              ? `${inv.duracionAccesoDias} días`
                              : "Tiempo indefinido"}
                          </span>
                          <span className="etiqueta por-vencer">
                            Vence el {formatearFecha(inv.venceUtc)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="acceso-item-acciones">
                      <button
                        type="button"
                        className="boton-chico"
                        onClick={() => {
                          setInvitacionReciente(inv);
                          setPestana("generar");
                        }}
                      >
                        Ver QR
                      </button>
                      <button
                        type="button"
                        className="boton-chico err"
                        disabled={mutacionCancelarInv.isPending}
                        onClick={() => {
                          if (confirm("¿Deseas anular esta invitación?")) {
                            mutacionCancelarInv.mutate(inv.invitacionID);
                          }
                        }}
                      >
                        ✕ Anular enlace
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
